// @flow

/**
 * Module dependencies.
 */

import { Amount, Input } from 'client/components/core/forms';
import { ErrorMessage } from 'client/components/forms/components/messages';
import { LoadingButton } from 'client/components/core/buttons';
import { checkAmount, formatAmount, getNumbers, isEmail } from 'client/core/utils';
import { colors, units } from 'client/styles';
import { compose, defaultProps } from 'recompose';
import { find, get, includes, lowerCase } from 'lodash';
import { translator } from 'core/utils';
import CurrenciesFromTo from 'client/components/forms/components/currencies-from-to';
import FormContainer from 'client/components/forms/components/form-container';
import Layout from 'client/components/core/layout';
import Loader from 'client/components/forms/components/loader';
import Panel from 'client/components/forms/components/panel';
import RawHtml from 'react-raw-html';
import React, { Component } from 'react';
import Text from 'client/components/core/type';
import config from 'config';
import formSubmitWrapper from 'client/components/forms/components/form-submit-wrapper';
import styled from 'styled-components';
import zendeskEntities from 'client/core/entities/zendesk';

/**
 * `Props` type.
 */

type Props = {
  autofocus?: boolean,
  fluid: boolean,
  fullfilled: boolean,
  onReset: Function,
  onSubmit: Function,
  pending: boolean,
  submissionError: boolean,
  translate: Function
};

/**
 * `TextDetails` component.
 */

// $FlowFixMe
const TextDetails = styled(Text.Small)`
  display: block;
  line-height: 18px;
  margin-bottom: 0;
`;

/**
 * `FormTransferRequest` component.
 */

class FormTransferRequest extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * State.
   */

  state: Object = {
    amount: 0,
    currencies: [],
    email: '',
    reason: '',
    switchedCurrencies: false,
    toCurrencyPrefix: ''
  };

  /**
   * Reset form fields.
   */

  resetForm = (event: any) => {
    event.preventDefault();

    this.setInitialState();
  }

  /**
   * Get amount fields.
   */

  getAmountFields(): Object {
    const { currencies, fromCurrency } = this.state;
    const currency = find(currencies, ['value', fromCurrency]);
    const currencyCode = lowerCase(get(currency, 'code'));

    return {
      currencyCode,
      currencySymbol: get(currency, 'symbol'),
      minimumValue: get(config, `amount.minimum.${currencyCode}`),
      thousandSeparator: ','
    };
  }

  /**
   * Is form invalid.
   */

  isFormInvalid = (): boolean => {
    const { amount, email } = this.state;
    const amountValue = getNumbers(amount);
    const { minimumValue } = this.getAmountFields();

    return !amountValue.length || !email.length || !isEmail(email) || !checkAmount(amountValue, minimumValue);
  }

  /**
   * Get normalized list.
   */

  getCurrenciesOptions = (switchedGroup: boolean) => {
    const { currencies, currenciesGroup1, currenciesGroup2 } = this.state;
    const currenciesGroup = switchedGroup ? currenciesGroup2 : currenciesGroup1;

    return currencies
      .filter(({ code }) => includes(currenciesGroup, code))
      .map(({ code, value }) => ({ label: code, value }));
  }

  /**
   * Set initial state.
   */

  setInitialState = () => {
    const { onReset, translate } = this.props;
    const { currencies, currenciesGroup1, currenciesGroup2, defaultValues } = zendeskEntities(translate);
    const { amount, fromCurrency, reason, switchedCurrencies, toCurrency, toCurrencyPrefix } = defaultValues;

    this.setState({
      amount,
      currencies,
      currenciesGroup1,
      currenciesGroup2,
      email: '',
      fromCurrency,
      reason,
      switchedCurrencies,
      toCurrency,
      toCurrencyPrefix
    }, onReset);
  }

  /**
   * Handle switch currencies.
   */

  handleSwitchCurrencies = () => {
    const { amount, fromCurrency, switchedCurrencies, toCurrency } = this.state;

    this.setState({
      amount,
      fromCurrency: toCurrency,
      switchedCurrencies: !switchedCurrencies,
      toCurrency: fromCurrency
    });
  }

  /**
   * Handle input change.
   */

  handleInputChange = ({ target }: any) => {
    const { value, name } = target;

    this.setState({
      [name]: value
    });
  }

  /**
   * Normalized form data.
   */

  normalizedFormData = () => {
    const {
      amount,
      email,
      fromCurrency,
      reason,
      toCurrency,
      toCurrencyPrefix
    } = this.state;

    return {
      amount,
      email,
      fromCurrency,
      reason,
      toCurrency: `${toCurrencyPrefix}${toCurrency}`
    };
  }

  /**
   * Handle form submit.
   */

  handleSubmit = (event: any) => {
    event.preventDefault();

    if (this.isFormInvalid()) {
      return;
    }

    const data = this.normalizedFormData();

    this.props.onSubmit(data);
  };

  /**
   * Component will mount.
   */

  componentWillMount() {
    this.setInitialState();
  }

  /**
   * Render
   */

  render(): React$Element<*> {
    const {
      autofocus,
      fluid,
      fullfilled,
      pending,
      submissionError,
      translate
    } = this.props;

    const {
      amount,
      fromCurrency,
      reason,
      switchedCurrencies,
      toCurrency
    } = this.state;

    const optionsFrom = this.getCurrenciesOptions(switchedCurrencies);
    const optionsTo = this.getCurrenciesOptions(!switchedCurrencies);
    const {
      currencySymbol,
      minimumValue,
      thousandSeparator
    } = this.getAmountFields();

    return (
      <Panel fluid={fluid}>
        <Loader isLoading={pending} />

        <FormContainer
          formVisible={!pending && !fullfilled}
          resetForm={this.resetForm}
          successVisible={fullfilled}
        >
          <Text.H4 color={colors.shadesOfBlue.bigStoneDarker}>
            {translate('formRequest.title')}
          </Text.H4>

          <form onSubmit={this.handleSubmit}>
            <Amount
              autoFocus={autofocus}
              label={translate('formRequest.labelAmount')}
              minimumValue={minimumValue}
              name={'amount'}
              onUpdate={this.handleInputChange}
              prefix={currencySymbol}
              thousandSeparator={thousandSeparator}
              tooltip={translate('formRequest.tooltipAmount', {
                amount: formatAmount(minimumValue, currencySymbol, thousandSeparator)
              })}
              validation={checkAmount}
              value={amount}
            />

            <CurrenciesFromTo
              fromCurrency={fromCurrency}
              fromCurrencyList={optionsFrom}
              inputChange={this.handleInputChange}
              labelFrom={translate('formRequest.labelFrom')}
              labelTo={translate('formRequest.labelTo')}
              swithCurrencies={this.handleSwitchCurrencies}
              toCurrency={toCurrency}
              toCurrencyList={optionsTo}
            />

            <Layout marginBottom={units(3)}>
              <Input
                label={translate('formRequest.labelEmail')}
                name={'email'}
                onUpdate={this.handleInputChange}
                type={'email'}
                validation={isEmail}
                value={this.state.email}
              />
            </Layout>

            <input
              name={'reason'}
              type={'hidden'}
              value={reason}
            />

            <Layout marginBottom={units(2.5)}>
              <LoadingButton
                disabled={this.isFormInvalid()}
                element={'button'}
                loading={pending}
                type={'submit'}
              >
                {translate('formRequest.buttonSubmit')}
              </LoadingButton>
            </Layout>

            <ErrorMessage
              message={translate('formRequest.message.error')}
              visible={submissionError}
            />

            <TextDetails color={colors.shadesOfBlue.paleSky}>
              <RawHtml.div>
                {translate('formRequest.terms')}
              </RawHtml.div>
            </TextDetails>
          </form>
        </FormContainer>
      </Panel>
    );
  }

}

/**
 * Export `FormTransferRequest`.
 */

export default compose(
  defaultProps({
    autofocus: false,
    fluid: false
  }),
  translator()
)(formSubmitWrapper(FormTransferRequest));



// WEBPACK FOOTER //
// ../src/client/components/forms/form-transfer-request.js